//src/utils/opaque-utils.ts

import { OpaqueClient } from '@cloudflare/opaque-ts';

import type { OpaqueResult } from "@/types/opaque.types";
import { OPAQUE_CONFIG } from '@/lib/opaque-config';

import { isNumberArray } from './type-guards';

// Constants for hex encoding/decoding
const HEX_RADIX = 16;
const HEX_CHAR_LENGTH = 2;
const HEX_PAD_CHAR = '0';
const EVEN_LENGTH_REMAINDER = 0;

// Constants for validation
export const EMPTY_LENGTH = 0;

/**
 * Convert byte array to hex string (browser-compatible)
 */
export function bytesToHex(bytes: Uint8Array | number[]): string {
  const byteArray = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  return Array.from(byteArray)
    .map(byte => byte.toString(HEX_RADIX).padStart(HEX_CHAR_LENGTH, HEX_PAD_CHAR))
    .join('');
}

/**
 * Convert hex string to byte array (browser-compatible)
 * Returns a plain number[] array as expected by OPAQUE library
 */
export function hexToBytes(hex: string): number[] {
  if (hex.length % HEX_CHAR_LENGTH !== EVEN_LENGTH_REMAINDER) {
    throw new Error('Invalid hex string: length must be even');
  }

  const bytes: number[] = [];
  const INITIAL_INDEX = 0;
  for (let i = INITIAL_INDEX; i < hex.length; i += HEX_CHAR_LENGTH) {
    bytes.push(parseInt(hex.slice(i, i + HEX_CHAR_LENGTH), HEX_RADIX));
  }
  return bytes;
}

/**
 * Type guard to check if result is an OpaqueResult
 */
function isOpaqueResult<T>(result: unknown): result is OpaqueResult<T> {
  return (
    typeof result === 'object' &&
    result !== null &&
    'ok' in result &&
    typeof result.ok === 'boolean'
  );
}

/**
 * Type guard to check if OpaqueResult is successful
 */
function isOpaqueSuccess<T>(result: OpaqueResult<T>): result is { ok: true; value: T } {
  return result.ok && 'value' in result;
}

/**
 * Type guard to check if OpaqueResult is an error
 */
function isOpaqueError<T>(result: OpaqueResult<T>): result is { ok: false; error: string } {
  return !result.ok && 'error' in result;
}

/**
 * Unwrap OPAQUE result (handles both Result<T> and T | Error)
 */
export function unwrapResult<T>(
  result: T | Error | OpaqueResult<T>,
  errorMessage: string
): T {
  // Handle Error type first
  if (result instanceof Error) {
    throw new Error(`${errorMessage}: ${result.message}`);
  }

  // Handle OpaqueResult<T> type with proper type guards
  if (isOpaqueResult<T>(result)) {
    if (isOpaqueSuccess(result)) {
      return result.value;
    }
    if (isOpaqueError(result)) {
      throw new Error(`${errorMessage}: ${result.error}`);
    }
    // This should never happen if OpaqueResult type is correct
    throw new Error(`${errorMessage}: Invalid OPAQUE result structure`);
  }
  // Handle direct T type - no assertion needed, TypeScript can infer
  // At this point, result is neither Error nor OpaqueResult, so it must be T
  return result;
}

/**
 * Creates a new OPAQUE client instance with project configuration
 * @returns Configured OpaqueClient instance
 */
export function createOpaqueClient(): OpaqueClient {
  return new OpaqueClient(OPAQUE_CONFIG);
}

/**
 * Converts various serialization formats to Uint8Array
 */
export function toUint8Array(serialized: unknown): Uint8Array {
  if (serialized instanceof Uint8Array) {
    return serialized;
  }

  if (isNumberArray(serialized)) {
    return new Uint8Array(serialized);
  }

  return new Uint8Array();
}

